import React from "react";
import { Modal2 } from "@iq/models";
import { LuPlus, LuTrash } from "react-icons/lu";
import { saveProductAttributes } from "../../services/productService";
import { createAttributeDrafts, validateAttributes, parsePriceAdjustment, parseDurationAdjustment } from "./attributeDraft";
import "./addAttributesModal.css";

let draftCounter = 0;
const nextId = () => `attribute-draft-${++draftCounter}`;
const createOption = () => ({ clientId: nextId(), value: "", price: "0.00", duration: "0" });
const createAttribute = () => ({ clientId: nextId(), name: "", required: false, values: [createOption()] });

function Field({ label, error, id, className = "", ...props }) {
    return (
        <div className={`addAttributesField${className ? ` ${className}` : ""}`}>
            <label htmlFor={id}>{label}</label>
            <input id={id} className="addAttributesInput" aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} {...props} />
            {error && <span id={`${id}-error`} className="addAttributesFieldError">{error}</span>}
        </div>
    );
}

export default function AddAttributesModal({ isOpen, productId, attributes = [], selectedAttributeId = null, onClose = () => {}, onSaved = () => {} }) {
    const [rows, setRows] = React.useState([]);
    const [selectedId, setSelectedId] = React.useState(null);
    const [mobileEditor, setMobileEditor] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [committed, setCommitted] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const [error, setError] = React.useState("");
    const [focusId, setFocusId] = React.useState(null);
    const hostRef = React.useRef(null);
    const busyRef = React.useRef(false);
    const savedGroupsRef = React.useRef(null);
    const initialRowsRef = React.useRef("[]");
    const openingRef = React.useRef({ attributes, selectedAttributeId });
    openingRef.current = { attributes, selectedAttributeId };

    React.useEffect(() => {
        if (!isOpen) return undefined;
        const previouslyFocused = document.activeElement;
        const drafts = createAttributeDrafts(openingRef.current.attributes);
        const preselected = drafts.find((row) => String(row.attributeId) === String(openingRef.current.selectedAttributeId));
        const first = preselected || drafts[0];
        initialRowsRef.current = JSON.stringify(drafts);
        setRows(drafts);
        setSelectedId(first?.clientId ?? null);
        setMobileEditor(Boolean(preselected));
        setSaving(false);
        setCommitted(false);
        setSubmitted(false);
        setError("");
        setFocusId(first ? `${first.clientId}-name` : "add-attribute");
        busyRef.current = false;
        savedGroupsRef.current = null;
        return () => {
            if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) previouslyFocused.focus();
        };
    }, [isOpen]);

    React.useEffect(() => {
        if (isOpen && focusId) document.getElementById(focusId)?.focus();
    }, [isOpen, focusId, selectedId, mobileEditor]);

    React.useEffect(() => {
        if (saving) hostRef.current?.querySelector('[aria-label="Close modal"]')?.focus();
    }, [saving]);

    const validation = React.useMemo(() => validateAttributes(rows), [rows]);
    const selected = rows.find((row) => row.clientId === selectedId);
    const locked = saving || committed;
    const dirty = JSON.stringify(rows) !== initialRowsRef.current;
    const errorsFor = (row) => submitted ? validation.errors[row.clientId] : undefined;
    const updateRow = (id, changes) => setRows((current) => current.map((row) => row.clientId === id ? { ...row, ...changes } : row));
    const updateOption = (optionId, changes) => updateRow(selectedId, {
        values: selected.values.map((option) => option.clientId === optionId ? { ...option, ...changes } : option),
    });
    const selectRow = (row) => {
        setSelectedId(row.clientId);
        setMobileEditor(true);
        setFocusId(`${row.clientId}-name`);
    };
    const addRow = () => {
        const row = createAttribute();
        setRows((current) => [...current, row]);
        selectRow(row);
    };
    const requestClose = () => {
        if (busyRef.current) return;
        if (!committed && dirty && !window.confirm("Discard these unsaved attributes and options?")) return;
        onClose();
    };
    const removeRow = () => {
        const remaining = rows.filter((row) => row.clientId !== selectedId);
        setRows(remaining);
        if (remaining.length) selectRow(remaining[0]);
        else {
            setSelectedId(null);
            setMobileEditor(false);
            setFocusId("add-attribute");
        }
    };
    const addOption = () => {
        const option = createOption();
        updateRow(selectedId, { values: [...selected.values, option] });
        setFocusId(`${option.clientId}-value`);
    };
    const removeOption = (optionId) => {
        const index = selected.values.findIndex((option) => option.clientId === optionId);
        const values = selected.values.filter((option) => option.clientId !== optionId);
        updateRow(selectedId, { values });
        setFocusId(values.length ? `${values[Math.min(index, values.length - 1)].clientId}-value` : "add-option");
    };

    const handleSave = async () => {
        if (busyRef.current) return;
        const id = Number(productId);
        if (!Number.isSafeInteger(id) || id <= 0) {
            setError("A valid service is required before adding attributes.");
            return;
        }
        if (!committed) {
            setSubmitted(true);
            if (!validation.valid) {
                setError("Check the highlighted fields. Every attribute needs a name and at least one valid option.");
                const invalid = rows.find((row) => validation.errors[row.clientId]);
                if (invalid) {
                    selectRow(invalid);
                    const fieldId = validation.errors[invalid.clientId].firstFieldId;
                    setFocusId(fieldId || `${invalid.clientId}-name`);
                }
                return;
            }
        }
        busyRef.current = true;
        setSaving(true);
        setError("");
        if (!committed) {
            const groups = validation.groups;
            try {
                await saveProductAttributes(id, groups);
            } catch (saveError) {
                setError(saveError?.message || "Failed to save attributes.");
                busyRef.current = false;
                setSaving(false);
                return;
            }
            savedGroupsRef.current = groups;
            setCommitted(true);
        }
        try {
            await onSaved({ attributes: savedGroupsRef.current, attributeGroups: savedGroupsRef.current });
        } catch (refreshError) {
            setError(`Attributes were saved, but the page could not refresh: ${refreshError?.message || "Please try again."}`);
        } finally {
            busyRef.current = false;
            setSaving(false);
        }
    };

    const handleKeys = (event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            requestClose();
        }
        if (event.key !== "Tab") return;
        const focusable = Array.from(hostRef.current.querySelectorAll("button:not(:disabled), input:not(:disabled), [tabindex='0']"))
            .filter((element) => element.getClientRects().length > 0);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!focusable.includes(document.activeElement)) {
            event.preventDefault();
            first?.focus();
        } else if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
        }
    };

    if (!isOpen) return null;

    return (
        <div ref={hostRef} aria-busy={saving} className="addAttributesHost"
            onKeyDown={handleKeys}
        >
            <Modal2 isOpen={isOpen} onClose={requestClose} title="Manage attributes"
                styles={{
                    root: { padding: "var(--space-2)" },
                    panel: { width: "min(96vw, 1080px)", height: "min(94dvh, 760px)", minWidth: 0, minHeight: 0 },
                    content: { flex: 1, minHeight: 0, overflow: "hidden" },
                    header: { flexShrink: 0 },
                    footer: { flexShrink: 0 },
                    error: { flexShrink: 0, maxHeight: "5rem", overflowY: "auto" },
                }}
                actionButtons={[
                    { key: "cancel", text: committed ? "Close" : "Cancel", onClick: requestClose, disabled: saving,
                        style: { background: "var(--surface-1)", color: "var(--fg-default)", borderColor: "var(--border-default)" } },
                    { key: "save", text: saving ? "Saving..." : committed ? "Retry refresh" : "Save changes", onClick: handleSave, disabled: saving || (!committed && !dirty) },
                ]}
                errorMessage={error}
            >
                <div className="addAttributesModalContainer">
                    <div className="addAttributesSplit">
                        <aside className={`addAttributesRail${mobileEditor ? " addAttributesRailHidden" : ""}`} aria-label="Draft attributes">
                            <div className="addAttributesSectionHeading"><h2 className="addAttributesSectionTitle">Attributes</h2><span className="addAttributesCount">{rows.length}</span></div>
                            <div className="addAttributesList">
                                {rows.map((row) => (
                                    <button type="button" key={row.clientId} disabled={locked} onClick={() => selectRow(row)}
                                        className={`addAttributesListItem${row.clientId === selectedId ? " addAttributesSelected" : ""}`}
                                        aria-pressed={row.clientId === selectedId}
                                    >
                                        <strong className="addAttributesListItemName">{row.name.trim() || "Untitled attribute"}</strong>
                                        <span className="addAttributesListItemMeta">{row.values.length} {row.values.length === 1 ? "option" : "options"}{row.required ? " · Required" : ""}</span>
                                        {errorsFor(row) && <span className="addAttributesFieldError">Needs attention</span>}
                                    </button>
                                ))}
                                {!rows.length && <p className="addAttributesMuted">Start with an attribute such as Length or Material.</p>}
                            </div>
                            <button id="add-attribute" type="button" className="addAttributesSecondaryButton" disabled={locked} onClick={addRow}>
                                <LuPlus aria-hidden="true" /> Add attribute
                            </button>
                        </aside>
                        <section className={`addAttributesEditor${mobileEditor ? "" : " addAttributesEditorHidden"}`} aria-label="Attribute editor">
                            {selected ? (
                                <>
                                    <button type="button" className="addAttributesBack" disabled={saving} onClick={() => {
                                        setMobileEditor(false);
                                        setFocusId("add-attribute");
                                    }}>Back to attributes</button>
                                    <fieldset disabled={locked} className="addAttributesFields">
                                        <div className="addAttributesNameRow">
                                            <Field id={`${selected.clientId}-name`} label="Attribute name" value={selected.name}
                                                error={errorsFor(selected)?.name} placeholder="e.g. Length"
                                                onChange={(event) => updateRow(selectedId, { name: event.target.value })} />
                                            <button type="button" className="addAttributesIconButton" aria-label="Remove attribute" onClick={removeRow}><LuTrash aria-hidden="true" /></button>
                                        </div>
                                        <label className="addAttributesRequired">
                                            <input type="checkbox" className="addAttributesCheckbox" checked={selected.required} onChange={(event) => updateRow(selectedId, { required: event.target.checked })} />
                                            Customer must choose an option
                                        </label>
                                        <div className="addAttributesSectionHeading"><h2 className="addAttributesSectionTitle">Options</h2></div>
                                        {errorsFor(selected)?.values && <p className="addAttributesFieldError">{errorsFor(selected).values}</p>}
                                        <div className="addAttributesOptionList">
                                            {selected.values.map((option, index) => {
                                                const optionErrors = errorsFor(selected)?.options[option.clientId];
                                                const cents = parsePriceAdjustment(option.price);
                                                const minutes = parseDurationAdjustment(option.duration);
                                                return (
                                                    <div key={option.clientId} className="addAttributesOptionRow">
                                                        <Field id={`${option.clientId}-value`} className="addAttributesOptionName" label="Option" value={option.value} placeholder="e.g. Short"
                                                            error={optionErrors?.value} onChange={(event) => updateOption(option.clientId, { value: event.target.value })} />
                                                        <Field id={`${option.clientId}-price`} label="Price adjustment ($)" inputMode="text" value={option.price}
                                                            error={optionErrors?.price} onChange={(event) => updateOption(option.clientId, { price: event.target.value })} />
                                                        <Field id={`${option.clientId}-duration`} label="Time adjustment (min)" inputMode="text" value={option.duration}
                                                            error={optionErrors?.duration} onChange={(event) => updateOption(option.clientId, { duration: event.target.value })} />
                                                        <button type="button" className="addAttributesIconButton addAttributesOptionRemove" aria-label={`Remove option ${index + 1}`} onClick={() => removeOption(option.clientId)}><LuTrash aria-hidden="true" /></button>
                                                        {cents !== null && minutes !== null && (
                                                            <small className="addAttributesAdjustmentPreview">
                                                                {cents === 0 ? "$0.00" : `${cents > 0 ? "+" : "-"}$${(Math.abs(cents) / 100).toFixed(2)}`}
                                                                {" / "}{minutes > 0 ? "+" : ""}{minutes} min
                                                            </small>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <button id="add-option" type="button" className="addAttributesSecondaryButton addAttributesButtonInline" onClick={addOption}><LuPlus aria-hidden="true" /> Add option</button>
                                    </fieldset>
                                </>
                            ) : (
                                <div className="addAttributesEmpty">
                                    <h2 className="addAttributesSectionTitle">Create your first attribute</h2>
                                    <p className="addAttributesMuted">Group choices like Short, Medium and Long under one attribute, then set each option's adjustments.</p>
                                </div>
                            )}
                        </section>
                    </div>
                </div>
            </Modal2>
        </div>
    );
}
